import { supabase }       from '../supabase.js';
import { pageQuery }      from '../lib/egress.js';
import { openDrillPanel, closeDrillPanel } from '../components/drill-panel.js';
import { buildStaffPayslipsPdf, downloadPdf, payslipFilename } from '../lib/staff-payslip-pdf.js';
import { openEmployeeFromElsewhere } from './hr-employees.js';

/* ============================================================
   HR · Payslips — salaried Londmarx employees (admin only).
   Contracted salary is NET; the DB grosses it up for PAYE + UIF
   (payroll_calculate_payslip). Extra earnings/deductions live in
   employee_payslip_lines and recalc via trigger.
============================================================ */

const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
const rands = n => 'R ' + Number(n || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS = ['January','February','March','April','May','June','July',
                'August','September','October','November','December'];

const SLIP_SELECT = '*, employees(*), employee_payslip_lines(*)';

let _state = null;
let _org   = null;

async function getOrg() {
  if (_org) return _org;
  const { data } = await pageQuery('org_settings');
  _org = data?.[0] || {};
  return _org;
}

async function latestPeriod() {
  const { data } = await supabase.from('employee_payslips')
    .select('period_year, period_month')
    .order('period_year',  { ascending: false })
    .order('period_month', { ascending: false })
    .limit(1);
  if (data?.[0]) return { year: data[0].period_year, month: data[0].period_month };
  const t = new Date();
  return { year: t.getFullYear(), month: t.getMonth() + 1 };
}

/* ── Main render ────────────────────────────────────────────── */
export async function renderHrPayslips() {
  const el = document.getElementById('view-hr-payslips');

  if (!window.__access?.hr) {
    el.innerHTML = `<div class="page-header"><h1>Payslips</h1><p>Staff HR is not switched on for you.</p></div>`;
    return;
  }

  if (!_state) _state = { ...(await latestPeriod()) };
  const state = _state;
  const org   = await getOrg();
  const orgMissing = [
    !org.company_registration && 'registration number',
    !org.company_address      && 'physical address',
  ].filter(Boolean);

  el.innerHTML = `
<style>
  .sp-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; font-size:13px; font-weight:500;
    border:1px solid var(--border); border-radius:8px; background:var(--surface); color:var(--text);
    cursor:pointer; white-space:nowrap; }
  .sp-btn:hover:not(:disabled) { background:var(--surface-2); }
  .sp-btn:disabled { opacity:.5; cursor:default; }
  .sp-btn.primary { background:var(--orange); border-color:var(--orange); color:#fff; }
  .sp-btn.primary:hover:not(:disabled) { background:var(--orange-dark); }
  .sp-badge { display:inline-block; padding:2px 9px; border-radius:12px; font-size:.72rem; font-weight:600; }
  .sp-badge.draft { background:#f1f5f9; color:#475569; }
  .sp-badge.final { background:#dbeafe; color:#1e40af; }
  .sp-badge.paid  { background:#d1fae5; color:#065f46; }
  .sp-totals { display:grid; grid-template-columns:repeat(auto-fit,minmax(130px,1fr)); gap:12px; padding:14px 18px;
    background:var(--surface); border:1px solid var(--border); border-radius:10px; margin-bottom:16px; }
  .sp-totals .lbl { font-size:.72rem; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:.03em; }
  .sp-totals .val { font-size:1.05rem; font-weight:700; margin-top:2px; font-variant-numeric:tabular-nums; }
  #sp-tbody td { vertical-align:middle; }
  #sp-tbody td.num, .sp-num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
  .sp-warn { padding:10px 14px; border:1px solid #fcd34d; background:#fffbeb; color:#92400e;
    border-radius:8px; font-size:.82rem; margin-bottom:14px; }
  .sp-table-wrap { overflow-x:auto; }
  .sp-kv { display:grid; grid-template-columns:1fr auto; gap:6px 12px; font-size:.86rem; }
  .sp-kv .muted { color:var(--text-muted); }
  .sp-kv .total { font-weight:700; border-top:1px solid var(--border); padding-top:6px; }
  .sp-line { display:flex; align-items:center; gap:8px; padding:6px 0; border-bottom:1px solid var(--border); font-size:.84rem; }
  .sp-line .desc { flex:1; }
  .sp-del { border:none; background:none; color:#b91c1c; cursor:pointer; font-size:.8rem; }
  .sp-form { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
  .sp-form label { display:flex; flex-direction:column; gap:3px; font-size:.74rem; font-weight:600; color:var(--text-muted); }
  .sp-form input, .sp-form select { padding:6px 8px; border:1px solid var(--border); border-radius:6px;
    background:var(--surface); color:var(--text); font-size:.84rem; }
  .sp-form .full { grid-column:1 / -1; }
  .sp-msg { font-size:.8rem; margin-top:6px; }
</style>

<div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px">
  <div>
    <h1>Payslips</h1>
    <p>Employee payslips (staff only, not learners). Contracted pay is take-home; gross is calculated for PAYE + UIF.</p>
  </div>
  <div style="display:flex;gap:8px;flex-wrap:wrap">
    <button class="sp-btn" id="sp-generate" title="Create missing payslips and recalculate drafts">Generate / recalculate</button>
    <button class="sp-btn primary" id="sp-download-all">Download all PDFs</button>
  </div>
</div>

${orgMissing.length ? `<div class="sp-warn">Company ${orgMissing.join(' and ')} not set in org_settings, so payslips will print without them.</div>` : ''}

<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:16px">
  <label style="display:flex;align-items:center;gap:6px;font-size:.78rem;font-weight:600;color:var(--text-muted)">
    Month
    <input type="month" id="sp-month" value="${state.year}-${String(state.month).padStart(2, '0')}"
      style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface);color:var(--text);font-size:.78rem">
  </label>
  <span style="flex:1"></span>
  <span style="font-size:.78rem;color:var(--text-muted)">Set all this month to:</span>
  <button class="sp-btn sp-status-all" data-status="draft">Draft</button>
  <button class="sp-btn sp-status-all" data-status="final">Final</button>
  <button class="sp-btn sp-status-all" data-status="paid">Paid</button>
</div>

<div class="sp-totals" id="sp-totals"><span style="color:var(--text-muted);font-size:.83rem">Loading…</span></div>

<div class="table-card sp-table-wrap">
  <table class="data-table">
    <thead><tr>
      <th>Emp No</th><th>Name</th><th>ID Number</th>
      <th class="sp-num">Gross</th><th class="sp-num">PAYE</th><th class="sp-num">UIF</th>
      <th class="sp-num">Other</th><th class="sp-num">Nett Pay</th><th>Status</th><th></th>
    </tr></thead>
    <tbody id="sp-tbody"><tr><td colspan="10" class="no-data">Loading…</td></tr></tbody>
  </table>
</div>`;

  let slips = [];

  async function reload() {
    const { data } = await pageQuery('employee_payslips', {
      select: SLIP_SELECT,
      eq: { period_year: state.year, period_month: state.month },
    });
    slips = (data || []).sort((a, b) =>
      (a.employees?.employee_number || '').localeCompare(b.employees?.employee_number || ''));
    renderRows();
    renderTotals();
  }

  function renderTotals() {
    const sum = k => slips.reduce((t, s) => t + Number(s[k] || 0), 0);
    const cost = sum('total_earnings') + sum('uif_employer') + sum('sdl');
    const cells = [
      ['Staff',          slips.length],
      ['Gross',          rands(sum('total_earnings'))],
      ['PAYE',           rands(sum('paye'))],
      ['UIF (employee)', rands(sum('uif_employee'))],
      ['Nett pay',       rands(sum('net_pay'))],
      ['Employer cost',  rands(cost)],
    ];
    document.getElementById('sp-totals').innerHTML = cells.map(([l, v]) =>
      `<div><div class="lbl">${l}</div><div class="val">${v}</div></div>`).join('');
  }

  function renderRows() {
    const tbody = document.getElementById('sp-tbody');
    if (!slips.length) {
      tbody.innerHTML = `<tr><td colspan="10" class="no-data">
        No payslips for ${MONTHS[state.month - 1]} ${state.year}. Click “Generate / recalculate” to create them.</td></tr>`;
      return;
    }
    tbody.innerHTML = slips.map(s => {
      const e = s.employees || {};
      return `
      <tr class="clickable-row" data-id="${esc(s.id)}">
        <td>${esc(e.employee_number)}</td>
        <td><strong>${esc(e.first_name)} ${esc(e.surname)}</strong></td>
        <td>${e.national_id ? esc(e.national_id) : '<span style="color:#b45309">Outstanding</span>'}</td>
        <td class="num">${rands(s.total_earnings)}</td>
        <td class="num">${rands(s.paye)}</td>
        <td class="num">${rands(s.uif_employee)}</td>
        <td class="num">${Number(s.other_deductions) ? rands(s.other_deductions) : '—'}</td>
        <td class="num"><strong>${rands(s.net_pay)}</strong></td>
        <td><span class="sp-badge ${esc(s.status)}">${esc(s.status)}</span></td>
        <td><button class="sp-btn sp-pdf" data-id="${esc(s.id)}" style="padding:3px 10px;font-size:.75rem">PDF</button></td>
      </tr>`;
    }).join('');

    tbody.querySelectorAll('tr.clickable-row').forEach(tr =>
      tr.addEventListener('click', ev => {
        if (ev.target.closest('.sp-pdf')) return;
        openSlip(tr.dataset.id);
      }));
    tbody.querySelectorAll('.sp-pdf').forEach(btn =>
      btn.addEventListener('click', async () => {
        const s = slips.find(x => x.id === btn.dataset.id);
        btn.disabled = true;
        try { downloadPdf(await buildStaffPayslipsPdf([s], await getOrg()), payslipFilename(s)); }
        finally { btn.disabled = false; }
      }));
  }

  /* ── Drill panel: breakdown, extra lines, employee details ── */
  async function refreshSlip(id) {
    const { data } = await pageQuery('employee_payslips', { select: SLIP_SELECT, eq: { id } });
    const fresh = data?.[0];
    if (fresh) slips = slips.map(s => (s.id === id ? fresh : s));
    renderRows();
    renderTotals();
    return fresh;
  }

  function openSlip(id) {
    const s = slips.find(x => x.id === id);
    if (!s) return;
    const e = s.employees || {};
    const editable = s.status === 'draft';
    const lines = [...(s.employee_payslip_lines || [])]
      .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));

    const html = `
      <div class="drill-section">
        <div class="drill-section-title">Breakdown</div>
        <div class="sp-kv">
          <span>Basic salary (grossed up)</span><span class="sp-num">${rands(s.basic_salary)}</span>
          ${lines.filter(l => l.line_type === 'earning').map(l =>
            `<span>${esc(l.description)}${l.is_taxable ? '' : ' <span class="muted">(non-taxable)</span>'}</span>
             <span class="sp-num">${rands(l.amount)}</span>`).join('')}
          <span class="total">Total earnings</span><span class="total sp-num">${rands(s.total_earnings)}</span>
          <span>PAYE</span><span class="sp-num">−${rands(s.paye)}</span>
          <span>UIF (1%)</span><span class="sp-num">−${rands(s.uif_employee)}</span>
          ${lines.filter(l => l.line_type === 'deduction').map(l =>
            `<span>${esc(l.description)}</span><span class="sp-num">−${rands(l.amount)}</span>`).join('')}
          <span class="total">Nett pay</span><span class="total sp-num">${rands(s.net_pay)}</span>
          <span class="muted">Contracted take-home</span><span class="muted sp-num">${rands(s.target_net)}</span>
          <span class="muted">Employer UIF + SDL</span><span class="muted sp-num">${rands(Number(s.uif_employer) + Number(s.sdl))}</span>
        </div>
      </div>

      <div class="drill-section">
        <div class="drill-section-title">Additional lines</div>
        ${lines.length ? lines.map(l => `
          <div class="sp-line">
            <span class="sp-badge ${l.line_type === 'earning' ? 'paid' : 'draft'}">${l.line_type}</span>
            <span class="desc">${esc(l.description)}${l.line_type === 'earning' && !l.is_taxable ? ' (non-taxable)' : ''}</span>
            <span class="sp-num">${rands(l.amount)}</span>
            ${editable ? `<button class="sp-del" data-line="${esc(l.id)}" title="Remove line">Remove</button>` : ''}
          </div>`).join('') : '<p style="font-size:.82rem;color:var(--text-muted)">None.</p>'}
        ${editable ? `
        <form id="sp-line-form" class="sp-form" style="margin-top:10px">
          <label>Type
            <select name="line_type">
              <option value="earning">Earning</option>
              <option value="deduction">Deduction</option>
            </select>
          </label>
          <label>Amount (R)
            <input name="amount" type="number" min="0" step="0.01" required>
          </label>
          <label class="full">Description
            <input name="description" required placeholder="e.g. Performance bonus, Staff loan repayment">
          </label>
          <label class="full" style="flex-direction:row;align-items:center;gap:6px">
            <input name="is_taxable" type="checkbox" checked> Taxable (earnings only)
          </label>
          <div class="full"><button class="sp-btn primary" type="submit">Add line</button></div>
          <div class="full sp-msg" id="sp-line-msg"></div>
        </form>` : `<p style="font-size:.8rem;color:var(--text-muted);margin-top:8px">
          This payslip is <strong>${esc(s.status)}</strong>. Set the month back to Draft to edit it.</p>`}
      </div>

      <div class="drill-section">
        <div class="drill-section-title">Employee</div>
        <div class="sp-kv">
          <span class="muted">ID number</span><span>${e.national_id ? esc(e.national_id) : 'Outstanding'}</span>
          <span class="muted">Occupation</span><span>${esc(e.occupation || '—')}</span>
          <span class="muted">Bank</span><span>${esc([e.bank_name, e.bank_account_number].filter(Boolean).join(' · ') || '—')}</span>
        </div>
        <button class="sp-btn" id="sp-edit-emp" style="margin-top:10px">Edit employee details</button>
      </div>`;

    openDrillPanel({
      eyebrow: `${e.employee_number} · ${MONTHS[s.period_month - 1]} ${s.period_year}`,
      title:   `${e.first_name} ${e.surname}`,
      html,
      onMount: body => wireSlipPanel(body, s),
    });
  }

  function wireSlipPanel(body, s) {
    body.querySelectorAll('.sp-del').forEach(btn =>
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        const { error } = await supabase.from('employee_payslip_lines').delete().eq('id', btn.dataset.line);
        if (error) { alert(error.message); btn.disabled = false; return; }
        await refreshSlip(s.id);
        openSlip(s.id);
      }));

    const lineForm = body.querySelector('#sp-line-form');
    lineForm?.addEventListener('submit', async ev => {
      ev.preventDefault();
      const fd  = new FormData(lineForm);
      const msg = body.querySelector('#sp-line-msg');
      const { error } = await supabase.from('employee_payslip_lines').insert({
        payslip_id:  s.id,
        line_type:   fd.get('line_type'),
        description: String(fd.get('description')).trim(),
        amount:      Number(fd.get('amount')),
        is_taxable:  fd.get('line_type') === 'earning' ? fd.get('is_taxable') === 'on' : true,
      });
      if (error) { msg.textContent = error.message; msg.style.color = '#b91c1c'; return; }
      await refreshSlip(s.id);
      openSlip(s.id);
    });

    body.querySelector('#sp-edit-emp').addEventListener('click', () => {
      closeDrillPanel();
      openEmployeeFromElsewhere(s.employee_id);
    });
  }

  /* ── Toolbar ─────────────────────────────────────────────── */
  document.getElementById('sp-month').addEventListener('change', ev => {
    const [y, m] = ev.target.value.split('-').map(Number);
    if (!y || !m) return;
    state.year = y; state.month = m;
    reload();
  });

  document.getElementById('sp-generate').addEventListener('click', async ev => {
    const btn = ev.currentTarget;
    btn.disabled = true;
    const { error } = await supabase.rpc('payroll_generate_month', { p_year: state.year, p_month: state.month });
    btn.disabled = false;
    if (error) { alert(error.message); return; }
    await reload();
  });

  document.getElementById('sp-download-all').addEventListener('click', async ev => {
    if (!slips.length) return;
    const btn = ev.currentTarget;
    btn.disabled = true;
    try {
      _org = null;   // pick up org_settings edits made since page load
      const bytes = await buildStaffPayslipsPdf(slips, await getOrg());
      downloadPdf(bytes, `staff-payslips-${state.year}-${String(state.month).padStart(2, '0')}.pdf`);
    } finally { btn.disabled = false; }
  });

  document.querySelectorAll('.sp-status-all').forEach(btn =>
    btn.addEventListener('click', async () => {
      if (!slips.length) return;
      const status = btn.dataset.status;
      if (!confirm(`Set all ${slips.length} payslips for ${MONTHS[state.month - 1]} ${state.year} to "${status}"?`)) return;
      const { error } = await supabase.from('employee_payslips')
        .update({ status })
        .eq('period_year', state.year).eq('period_month', state.month);
      if (error) { alert(error.message); return; }
      closeDrillPanel();
      await reload();
    }));

  await reload();
}
